import { useMemo, useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { PLAN_FEATURE_CATALOG } from '@neuroedge/shared';
import type { CreatePlanInput, Plan, PlanStatus, UpdatePlanInput } from '@neuroedge/shared';
import {
  Button,
  Card,
  CollapsibleSection,
  PageState,
  TextField,
  useToast,
} from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useAuth } from '../../auth/AuthContext';
import {
  usePlans,
  useCreatePlan,
  useUpdatePlan,
  usePlanFeatures,
  usePlanMenus,
  useSetPlanMenus,
  useSetPlanFeatures,
} from '../../api/billing';
import { useMenuCatalog } from '../../api/access-requests';
import { CollectionsSettingsCard } from './CollectionsCards';

const BLANK: CreatePlanInput = {
  code: '',
  name: '',
  description: '',
  monthlyFeeAmount: 0,
  currency: 'USD',
  messageAllowance: 0,
};

const PLAN_STATUSES: PlanStatus[] = ['ACTIVE', 'RETIRED', 'HIDDEN'];
const CODE_RE = /^[a-z0-9_-]+$/;

const LIMIT_FIELDS: { key: keyof LimitStrings; label: string }[] = [
  { key: 'maxWhatsappNumbers', label: 'Max WhatsApp numbers' },
  { key: 'maxTeamUsers', label: 'Max team users' },
  { key: 'maxContacts', label: 'Max contacts' },
  { key: 'maxAutomations', label: 'Max automations' },
  { key: 'aiDocsIncludedPerMonth', label: 'AI docs included / month' },
];

interface LimitStrings {
  maxWhatsappNumbers: string;
  maxTeamUsers: string;
  maxContacts: string;
  maxAutomations: string;
  aiDocsIncludedPerMonth: string;
}

/** Empty string = unlimited/custom (`null`). */
function limitToString(n: number | null | undefined): string {
  return n === null || n === undefined ? '' : String(n);
}

function parseLimit(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && Number.isInteger(n) && n >= 0 ? n : NaN;
}

/** Mirrors the backend's `createPlanBody` limits (billing.schemas.ts). */
function validate(form: CreatePlanInput): Record<string, string> {
  const errors: Record<string, string> = {};

  const code = form.code.trim();
  if (code.length < 1) errors.code = 'Enter a plan code.';
  else if (code.length > 60) errors.code = 'Must be 60 characters or fewer.';
  else if (!CODE_RE.test(code)) {
    errors.code = 'Use lowercase letters, numbers, hyphens, and underscores only (no spaces).';
  }

  const name = form.name.trim();
  if (name.length < 1) errors.name = 'Enter a plan name.';
  else if (name.length > 200) errors.name = 'Must be 200 characters or fewer.';

  if ((form.description ?? '').trim().length > 2000) {
    errors.description = 'Must be 2000 characters or fewer.';
  }

  if (!Number.isFinite(form.monthlyFeeAmount) || form.monthlyFeeAmount < 0) {
    errors.monthlyFeeAmount = 'Enter a monthly fee of 0 or more.';
  }

  const currency = form.currency.trim();
  if (currency.length !== 3) errors.currency = 'Enter a 3-letter currency code, e.g. USD.';

  if (
    !Number.isFinite(form.messageAllowance) ||
    !Number.isInteger(form.messageAllowance) ||
    form.messageAllowance < 0
  ) {
    errors.messageAllowance = 'Enter a whole number of 0 or more.';
  }

  return errors;
}

function validateLimits(limits: LimitStrings): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const { key, label } of LIMIT_FIELDS) {
    if (Number.isNaN(parseLimit(limits[key]))) {
      errors[key] = `${label}: enter a whole number of 0 or more, or leave blank for unlimited.`;
    }
  }
  return errors;
}

function CreatePlanForm() {
  const create = useCreatePlan();
  const toast = useToast();
  const [form, setForm] = useState(BLANK);
  const [limits, setLimits] = useState<LimitStrings>({
    maxWhatsappNumbers: '',
    maxTeamUsers: '',
    maxContacts: '',
    maxAutomations: '',
    aiDocsIncludedPerMonth: '',
  });
  const [isCustomPricing, setIsCustomPricing] = useState(false);
  const [isMostPopular, setIsMostPopular] = useState(false);
  const [showOnPricingPage, setShowOnPricingPage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const set = (key: keyof CreatePlanInput) => (e: { target: { value: string } }) => {
    const raw = e.target.value;
    setForm((f) => ({
      ...f,
      [key]: key === 'monthlyFeeAmount' || key === 'messageAllowance' ? Number(raw) || 0 : raw,
    }));
    setFieldErrors((f) => {
      if (!f[key]) return f;
      const { [key]: _removed, ...rest } = f;
      return rest;
    });
  };

  const setLimit = (key: keyof LimitStrings) => (e: { target: { value: string } }) => {
    setLimits((l) => ({ ...l, [key]: e.target.value }));
    setFieldErrors((f) => {
      if (!f[key]) return f;
      const { [key]: _removed, ...rest } = f;
      return rest;
    });
  };

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const errors = { ...validate(form), ...validateLimits(limits) };
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the highlighted fields.');
      toast.error('Please fix the highlighted fields.');
      return;
    }

    try {
      await create.mutateAsync({
        ...form,
        code: form.code.trim(),
        name: form.name.trim(),
        description: form.description?.trim() || undefined,
        isCustomPricing,
        isMostPopular,
        showOnPricingPage,
        maxWhatsappNumbers: parseLimit(limits.maxWhatsappNumbers),
        maxTeamUsers: parseLimit(limits.maxTeamUsers),
        maxContacts: parseLimit(limits.maxContacts),
        maxAutomations: parseLimit(limits.maxAutomations),
        aiDocsIncludedPerMonth: parseLimit(limits.aiDocsIncludedPerMonth),
      });
      setForm(BLANK);
      setLimits({
        maxWhatsappNumbers: '',
        maxTeamUsers: '',
        maxContacts: '',
        maxAutomations: '',
        aiDocsIncludedPerMonth: '',
      });
      setIsCustomPricing(false);
      setIsMostPopular(false);
      setFieldErrors({});
      toast.success('Plan created.');
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
          setError('Please fix the highlighted fields.');
          toast.error('Please fix the highlighted fields.');
        } else {
          setError(err.message);
          toast.error(err.message);
        }
      } else {
        const message = err instanceof Error ? err.message : 'Could not create the plan.';
        setError(message);
        toast.error(message);
      }
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      style={{ display: 'grid', gap: 'var(--space-3)', maxWidth: 420 }}
      noValidate
    >
      <TextField
        label="Code *"
        placeholder="growth"
        value={form.code}
        onChange={set('code')}
        error={fieldErrors.code}
      />
      <TextField
        label="Name *"
        placeholder="Growth"
        value={form.name}
        onChange={set('name')}
        error={fieldErrors.name}
      />
      <TextField
        label="Description"
        placeholder="For growing teams"
        value={form.description ?? ''}
        onChange={set('description')}
        error={fieldErrors.description}
      />
      <TextField
        label="Monthly fee *"
        type="number"
        min={0}
        step="0.01"
        value={String(form.monthlyFeeAmount)}
        onChange={set('monthlyFeeAmount')}
        error={fieldErrors.monthlyFeeAmount}
      />
      <TextField
        label="Currency *"
        placeholder="USD"
        maxLength={3}
        value={form.currency}
        onChange={set('currency')}
        error={fieldErrors.currency}
      />
      <TextField
        label="Message allowance *"
        type="number"
        min={0}
        value={String(form.messageAllowance)}
        onChange={set('messageAllowance')}
        error={fieldErrors.messageAllowance}
      />
      {LIMIT_FIELDS.map(({ key, label }) => (
        <TextField
          key={key}
          label={`${label} (blank = unlimited)`}
          type="number"
          min={0}
          value={limits[key]}
          onChange={setLimit(key)}
          error={fieldErrors[key]}
        />
      ))}
      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <input
          type="checkbox"
          checked={isCustomPricing}
          onChange={(e) => setIsCustomPricing(e.target.checked)}
        />
        Custom pricing (e.g. Enterprise — shows "Contact us", not a price)
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <input
          type="checkbox"
          checked={isMostPopular}
          onChange={(e) => setIsMostPopular(e.target.checked)}
        />
        "Most popular" badge
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <input
          type="checkbox"
          checked={showOnPricingPage}
          onChange={(e) => setShowOnPricingPage(e.target.checked)}
        />
        Show on the pricing page (public /pricing and the in-app "Plans" menu)
      </label>
      {error ? <p style={{ margin: 0, color: 'var(--color-danger)' }}>{error}</p> : null}
      <div>
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? 'Creating…' : 'Create plan'}
        </Button>
      </div>
    </form>
  );
}

function money(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

interface EditForm {
  name: string;
  description: string;
  monthlyFeeAmount: string;
  messageAllowance: string;
  isCustomPricing: boolean;
  isMostPopular: boolean;
  showOnPricingPage: boolean;
  limits: LimitStrings;
}

function editFormFrom(p: Plan): EditForm {
  return {
    name: p.name,
    description: p.description ?? '',
    monthlyFeeAmount: String(p.monthlyFeeAmount),
    messageAllowance: String(p.messageAllowance),
    isCustomPricing: p.isCustomPricing,
    isMostPopular: p.isMostPopular,
    showOnPricingPage: p.showOnPricingPage,
    limits: {
      maxWhatsappNumbers: limitToString(p.maxWhatsappNumbers),
      maxTeamUsers: limitToString(p.maxTeamUsers),
      maxContacts: limitToString(p.maxContacts),
      maxAutomations: limitToString(p.maxAutomations),
      aiDocsIncludedPerMonth: limitToString(p.aiDocsIncludedPerMonth),
    },
  };
}

function EditPlanForm({ plan }: { plan: Plan }) {
  const update = useUpdatePlan();
  const toast = useToast();
  const original = useMemo(() => editFormFrom(plan), [plan]);
  const [form, setForm] = useState<EditForm>(original);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const dirty =
    form.name !== original.name ||
    form.description !== original.description ||
    form.monthlyFeeAmount !== original.monthlyFeeAmount ||
    form.messageAllowance !== original.messageAllowance ||
    form.isCustomPricing !== original.isCustomPricing ||
    form.isMostPopular !== original.isMostPopular ||
    form.showOnPricingPage !== original.showOnPricingPage ||
    LIMIT_FIELDS.some(({ key }) => form.limits[key] !== original.limits[key]);

  const setField =
    (key: 'name' | 'description' | 'monthlyFeeAmount' | 'messageAllowance') =>
    (e: { target: { value: string } }) => {
      setForm((f) => ({ ...f, [key]: e.target.value }));
      setFieldErrors((f) => {
        if (!f[key]) return f;
        const { [key]: _removed, ...rest } = f;
        return rest;
      });
    };

  const setLimit = (key: keyof LimitStrings) => (e: { target: { value: string } }) => {
    setForm((f) => ({ ...f, limits: { ...f.limits, [key]: e.target.value } }));
    setFieldErrors((f) => {
      if (!f[key]) return f;
      const { [key]: _removed, ...rest } = f;
      return rest;
    });
  };

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!dirty) return;

    const fee = Number(form.monthlyFeeAmount);
    const allowance = Number(form.messageAllowance);
    const errors: Record<string, string> = { ...validateLimits(form.limits) };
    if (form.name.trim().length < 1) errors.name = 'Enter a plan name.';
    else if (form.name.trim().length > 200) errors.name = 'Must be 200 characters or fewer.';
    if (form.description.trim().length > 2000) {
      errors.description = 'Must be 2000 characters or fewer.';
    }
    if (!Number.isFinite(fee) || fee < 0) errors.monthlyFeeAmount = 'Enter a fee of 0 or more.';
    if (!Number.isFinite(allowance) || !Number.isInteger(allowance) || allowance < 0) {
      errors.messageAllowance = 'Enter a whole number of 0 or more.';
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the highlighted fields.');
      toast.error('Please fix the highlighted fields.');
      return;
    }

    const patch: UpdatePlanInput = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      monthlyFeeAmount: fee,
      messageAllowance: allowance,
      isCustomPricing: form.isCustomPricing,
      isMostPopular: form.isMostPopular,
      showOnPricingPage: form.showOnPricingPage,
      maxWhatsappNumbers: parseLimit(form.limits.maxWhatsappNumbers),
      maxTeamUsers: parseLimit(form.limits.maxTeamUsers),
      maxContacts: parseLimit(form.limits.maxContacts),
      maxAutomations: parseLimit(form.limits.maxAutomations),
      aiDocsIncludedPerMonth: parseLimit(form.limits.aiDocsIncludedPerMonth),
    };

    try {
      await update.mutateAsync({ planId: plan.id, patch });
      toast.success('Plan updated.');
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
          setError('Please fix the highlighted fields.');
          toast.error('Please fix the highlighted fields.');
        } else {
          setError(err.message);
          toast.error(err.message);
        }
      } else {
        const message = err instanceof Error ? err.message : 'Could not save the plan.';
        setError(message);
        toast.error(message);
      }
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      style={{ display: 'grid', gap: 'var(--space-3)', maxWidth: 420 }}
      noValidate
    >
      <TextField
        label="Name *"
        value={form.name}
        onChange={setField('name')}
        error={fieldErrors.name}
      />
      <TextField
        label="Description"
        value={form.description}
        onChange={setField('description')}
        error={fieldErrors.description}
      />
      <TextField
        label="Monthly fee *"
        type="number"
        min={0}
        step="0.01"
        value={form.monthlyFeeAmount}
        onChange={setField('monthlyFeeAmount')}
        error={fieldErrors.monthlyFeeAmount}
      />
      <TextField
        label="Message allowance *"
        type="number"
        min={0}
        value={form.messageAllowance}
        onChange={setField('messageAllowance')}
        error={fieldErrors.messageAllowance}
      />
      {LIMIT_FIELDS.map(({ key, label }) => (
        <TextField
          key={key}
          label={`${label} (blank = unlimited)`}
          type="number"
          min={0}
          value={form.limits[key]}
          onChange={setLimit(key)}
          error={fieldErrors[key]}
        />
      ))}
      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <input
          type="checkbox"
          checked={form.isCustomPricing}
          onChange={(e) => setForm((f) => ({ ...f, isCustomPricing: e.target.checked }))}
        />
        Custom pricing ("Contact us")
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <input
          type="checkbox"
          checked={form.isMostPopular}
          onChange={(e) => setForm((f) => ({ ...f, isMostPopular: e.target.checked }))}
        />
        "Most popular" badge
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <input
          type="checkbox"
          checked={form.showOnPricingPage}
          onChange={(e) => setForm((f) => ({ ...f, showOnPricingPage: e.target.checked }))}
        />
        Show on the pricing page (public /pricing and the in-app "Plans" menu)
      </label>
      {error ? <p style={{ margin: 0, color: 'var(--color-danger)' }}>{error}</p> : null}
      <div>
        <Button type="submit" disabled={!dirty || update.isPending}>
          {update.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </form>
  );
}

function FeaturesPanel({ planId }: { planId: string }) {
  const features = usePlanFeatures(planId);
  const setFeatures = useSetPlanFeatures(planId);
  const toast = useToast();
  const [pending, setPending] = useState<Set<string> | null>(null);

  if (features.isPending) return <PageState kind="loading" message="Loading features…" />;
  if (features.isError) return <PageState kind="error" message={features.error.message} />;

  const selected = pending ?? new Set(features.data.filter((f) => f.value).map((f) => f.key));
  const groups = [...new Set(PLAN_FEATURE_CATALOG.map((f) => f.groupLabel))];
  const dirty = pending !== null;

  function toggle(key: string) {
    setPending((prev) => {
      const next = new Set(prev ?? selected);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function save() {
    if (!pending) return;
    try {
      await setFeatures.mutateAsync(
        PLAN_FEATURE_CATALOG.map((f) => ({ key: f.key, value: pending.has(f.key) })),
      );
      setPending(null);
      toast.success('Features updated.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update features.');
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      {groups.map((group) => (
        <fieldset key={group} style={{ border: 'none', margin: 0, padding: 0 }}>
          <legend
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--color-text-muted)',
            }}
          >
            {group}
          </legend>
          <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
            {PLAN_FEATURE_CATALOG.filter((f) => f.groupLabel === group).map((f) => (
              <label
                key={f.key}
                style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
              >
                <input
                  type="checkbox"
                  checked={selected.has(f.key)}
                  onChange={() => toggle(f.key)}
                />
                {f.label}
              </label>
            ))}
          </div>
        </fieldset>
      ))}
      <div>
        <Button disabled={!dirty || setFeatures.isPending} onClick={() => void save()}>
          {setFeatures.isPending ? 'Saving…' : 'Save features'}
        </Button>
        {dirty ? (
          <Button
            variant="secondary"
            style={{ marginLeft: 'var(--space-2)' }}
            onClick={() => setPending(null)}
          >
            Discard changes
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Which menus a plan gives access to. Saving re-applies it to every
 * workspace on the plan at once (their admin overrides still apply).
 */
function PlanMenusPanel({ planId }: { planId: string }) {
  const catalog = useMenuCatalog();
  const menus = usePlanMenus(planId);
  const setMenus = useSetPlanMenus(planId);
  const toast = useToast();
  const [pending, setPending] = useState<Set<string> | null>(null);

  if (catalog.isPending || menus.isPending) return <PageState kind="loading" />;
  if (catalog.isError) return <PageState kind="error" message={catalog.error.message} />;
  if (menus.isError) return <PageState kind="error" message={menus.error.message} />;

  const selected = pending ?? new Set(menus.data.menuKeys);
  const groups = [...new Set(catalog.data.map((m) => m.groupLabel))];
  const dirty = pending !== null;

  function toggle(key: string) {
    setPending((prev) => {
      const next = new Set(prev ?? selected);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function save() {
    if (!pending) return;
    try {
      const r = await setMenus.mutateAsync([...pending]);
      setPending(null);
      toast.success(
        `Menus saved - applied to ${r.workspacesUpdated} workspace${r.workspacesUpdated === 1 ? '' : 's'} on this plan.`,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save the menus.');
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      <p style={{ margin: 0, color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
        Workspaces on this plan get these menus. Changes apply to them straight away; menus you
        switched on or off for one workspace (Tenants → Menu Access) still apply on top.
      </p>
      {groups.map((group) => (
        <fieldset key={group} style={{ border: 'none', margin: 0, padding: 0 }}>
          <legend
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--color-text-muted)',
            }}
          >
            {group}
          </legend>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
            {catalog.data
              .filter((m) => m.groupLabel === group)
              .map((m) => (
                <label
                  key={m.key}
                  style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(m.key)}
                    onChange={() => toggle(m.key)}
                  />
                  {m.label}
                </label>
              ))}
          </div>
        </fieldset>
      ))}
      <div>
        <Button disabled={!dirty || setMenus.isPending} onClick={() => void save()}>
          {setMenus.isPending ? 'Saving…' : 'Save menus'}
        </Button>
        {dirty ? (
          <Button
            variant="secondary"
            style={{ marginLeft: 'var(--space-2)' }}
            onClick={() => setPending(null)}
          >
            Discard changes
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function PlanRow({ plan }: { plan: Plan }) {
  const updatePlan = useUpdatePlan();
  const toast = useToast();

  async function onStatusChange(status: PlanStatus) {
    try {
      await updatePlan.mutateAsync({ planId: plan.id, patch: { status } });
      toast.success('Plan updated.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update the plan.');
    }
  }

  const summary = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
      <strong>{plan.name}</strong> ({plan.code}) —{' '}
      {plan.isCustomPricing ? 'Custom' : `${money(plan.monthlyFeeAmount, plan.currency)}/mo`} ·{' '}
      {plan.messageAllowance.toLocaleString()} messages
      {plan.isMostPopular ? <span className="ne-badge">MOST POPULAR</span> : null}
      {plan.showOnPricingPage ? <span className="ne-badge">On pricing page</span> : null}
    </span>
  );

  return (
    <li
      style={{
        borderBottom: '1px solid var(--color-border)',
        paddingBottom: 'var(--space-2)',
        listStyle: 'none',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-2)',
        }}
      >
        {summary}
        <select
          className="ne-input"
          style={{ width: 'auto' }}
          aria-label={`Status for ${plan.name}`}
          value={plan.status}
          onChange={(e) => void onStatusChange(e.target.value as PlanStatus)}
        >
          {PLAN_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      <CollapsibleSection title="Edit plan & limits">
        <EditPlanForm plan={plan} />
      </CollapsibleSection>
      <CollapsibleSection title="Menus included (access)">
        <PlanMenusPanel planId={plan.id} />
      </CollapsibleSection>
      <CollapsibleSection title="Features (pricing page)">
        <FeaturesPanel planId={plan.id} />
      </CollapsibleSection>
    </li>
  );
}

export function PlansPage() {
  const { isPlatformAdmin } = useAuth();
  const plans = usePlans();

  if (!isPlatformAdmin) return <Navigate to="/" replace />;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 760 }}>
      <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Pricing plans</h1>

      <Card title="Plans">
        {plans.isPending ? (
          <PageState kind="loading" message="Loading plans…" />
        ) : plans.isError ? (
          <PageState
            kind="error"
            message={plans.error.message}
            onRetry={() => void plans.refetch()}
          />
        ) : plans.data.length === 0 ? (
          <PageState kind="empty" title="No plans yet" message="Create one below." />
        ) : (
          <ul style={{ margin: 0, padding: 0, display: 'grid', gap: 'var(--space-3)' }}>
            {plans.data.map((p) => (
              <PlanRow key={p.id} plan={p} />
            ))}
          </ul>
        )}
      </Card>

      <CollectionsSettingsCard />

      <Card title="Create a plan">
        <CreatePlanForm />
      </Card>

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/admin/tenants">Tenants</Link> ·{' '}
        <Link to="/admin/plan-change-requests">Plan change requests</Link> ·{' '}
        <Link to="/">← Back to dashboard</Link>
      </p>
    </div>
  );
}
